import { Injectable } from '@angular/core';

export enum TopologyStyle {
  SingleLine = 'single-line',
  TwoLine = 'two-line',
  MultiLine = 'multi-line',
  WordIsLine = 'word-is-line',
}

export enum TextPosition {
  LEFT_TOP = 'left-top',
  LEFT_BOTTOM = 'left-bottom',
  CENTER = 'center',
  RIGHT_TOP = 'right-top',
  RIGHT_BOTTOM = 'right-bottom',
  CUSTOM = 'custom',
}

// Words that can be grouped with the previous word in multi-line mode
const GROUPABLE_WORDS = new Set([
  'is',
  'a',
  'an',
  'and',
  'for',
  'the',
  'to',
  'at',
  'in',
]);

@Injectable({ providedIn: 'root' })
export class TextTopologyProcessService {
  /**
   * Splits text into topology structure - an array of lines,
   * where each line is an array of words.
   * @param text The input text to process
   * @param style The topology style to apply
   * @returns Array of lines, each containing an array of words
   */

  getRandomLayout(text: string, topology: TopologyStyle): TextPosition {
    return TextPosition.LEFT_BOTTOM;
    const possibleLayouts = Object.values(TextPosition) as TextPosition[];

    const randomIndex = Math.floor(possibleLayouts.length * Math.random());
    return possibleLayouts[randomIndex] as TextPosition;
  }

  getRandomTopology(text: string, topology: TopologyStyle): TopologyStyle {
    const possibleTopologies = this.getPossibleTopologies(text).filter(
      t => t !== topology,
    );

    const randomIndex = Math.floor(possibleTopologies.length * Math.random());
    return possibleTopologies[randomIndex] as TopologyStyle;
  }

  /**
   * Returns all topology styles that produce different results for the given text.
   * @param text The input text to check
   * @returns Array of TopologyStyle values that give unique results
   */
  getPossibleTopologies(text: string): TopologyStyle[] {
    if (!text || text.trim() === '') {
      return [];
    }

    const allStyles = Object.values(TopologyStyle) as TopologyStyle[];
    const seenResults = new Map<string, TopologyStyle>();

    for (const style of allStyles) {
      const result = this.getTopology(text, style);
      // Serialize the result to compare // -- //
      const key = JSON.stringify(result);

      if (!seenResults.has(key)) {
        seenResults.set(key, style);
      }
    }

    return Array.from(seenResults.values());
  }

  getTopology(
    text: string,
    style: TopologyStyle = TopologyStyle.MultiLine,
  ): Array<string[]> {
    return text.split('\n').map(word => [word]);

    if (!text || text.trim() === '') {
      return [];
    }

    if (text.includes('&')) {
      return [
        text
          .trim()
          .split(/\s+/)
          .filter(word => word.length > 0),
      ];
    }

    // Get all words from the text
    const words = text
      .trim()
      .split(/\s+/)
      .filter(word => word.length > 0);

    if (words.length === 0) {
      return [];
    }

    switch (style) {
      case TopologyStyle.SingleLine:
        return this.singleLineTopology(words);
      case TopologyStyle.TwoLine:
        return this.twoLineTopology(words);
      case TopologyStyle.MultiLine:
        return this.multiLineTopology(words);
      case TopologyStyle.WordIsLine:
        return this.wordIsLineTopology(words);
      default:
        return this.multiLineTopology(words);
    }
  }

  private singleLineTopology(words: string[]): Array<string[]> {
    return [words];
  }

  private twoLineTopology(words: string[]): Array<string[]> {
    if (words.length <= 1) {
      return [words];
    }

    const midpoint = Math.ceil(words.length / 2);
    return [words.slice(0, midpoint), words.slice(midpoint)];
  }

  private multiLineTopology(words: string[]): Array<string[]> {
    const lines: Array<string[]> = [];
    let currentLine: string[] = [];

    for (const word of words) {
      const lowerWord = word.toLowerCase();
      const isGroupable = GROUPABLE_WORDS.has(lowerWord);

      if (currentLine.length === 0) {
        // Start a new line
        currentLine.push(word);
      } else if (isGroupable) {
        // Check if current line contains only groupable words
        const lastWord = currentLine[currentLine.length - 1].toLowerCase();
        const lastIsGroupable = GROUPABLE_WORDS.has(lastWord);

        if (lastIsGroupable) {
          // Continue the groupable sequence
          currentLine.push(word);
        } else {
          // Previous was non-groupable, start new line with this groupable word
          lines.push(currentLine);
          currentLine = [word];
        }
      } else {
        // Non-groupable word - always starts its own line
        lines.push(currentLine);
        currentLine = [word];
      }
    }

    // Don't forget the last line
    if (currentLine.length > 0) {
      lines.push(currentLine);
    }

    return lines;
  }

  private wordIsLineTopology(words: string[]): Array<string[]> {
    return words.map(word => [word]);
  }
}
